import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useBatch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext/BatchContext';
import { CardEntityError, useFormErrors } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, RawFieldData, UpdateField } from '@shared/scenes/entity/types';
import { toFieldJson } from '@shared/scenes/entity/util/toFieldJson';
import { useEntityFormViewTemplate } from '@shared/scenes/entity/util/useEntityFormViewTemplate';
import { useViewComponents } from '@shared/scenes/entity/util/useViewComponents';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { useCreateEntityFields } from './useCreateEntityFields';
import { useSaveNewEntity } from './useSaveNewCardEntity';

type Status = 'loading' | 'error' | 'input' | 'success';

interface CreateCardEntity {
  components: MBAPI.WorkflowTemplate_components[];
  fields: FieldRecord | null;
  errors: CardEntityError[];
  viewTemplateTitle: string;
  viewTemplate?: MBAPI.ViewTemplate;
  updateField: UpdateField;
  onPressSave: () => void;
  isLoading: boolean;
  status: Status;
  setStatus: (status: Status) => void;
  isDirty: boolean;
  resetForm: () => void;
  savedEntity?: MBAPI.WorkflowEntity;
}

export function useCreateCardEntity({
  workflowTemplateId,
  viewTemplateId,
  queryFieldOverrides,
}: {
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  queryFieldOverrides?: RawFieldData;
}): CreateCardEntity {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const viewTemplate = useEntityFormViewTemplate({
    workflowTemplateId,
    viewTemplateId,
    type: 'CREATE',
  });
  const [status, setStatus] = useState<Status>('loading');
  const [saveAttemptedWithLocalErrors, setSaveAttemptedWithLocalErrors] = useState(false);
  const [batchCreateError, setBatchCreateError] = useState<any>(null);
  const [savedEntity, setSavedEntity] = useState<MBAPI.WorkflowEntity>();
  const { shouldBatchCreate, batchCreate } = useBatch();
  const { fields, updateField, isDirty, resetFields } = useCreateEntityFields({
    workflowTemplate,
    viewTemplate,
    queryFieldOverrides,
  });

  const {
    saveEntity,
    loading: saveEntityLoading,
    errors: apiErrors,
  } = useSaveNewEntity(workflowTemplateId);

  const components = useViewComponents({
    workflowTemplate,
    viewTemplate,
    includeServerManagedComponents: false,
    includeInputComponentsOnly: true,
  });

  const { errors, isValid } = useFormErrors({
    apiErrors: batchCreateError ? [batchCreateError] : apiErrors,
    fields,
    components,
    includeOnSubmitErrors: saveAttemptedWithLocalErrors,
    includeLocalErrors: true,
  });

  useEffect(() => {
    setStatus(prevStatus => {
      const isFieldsReady = !isNil(fields);
      const isViewTemplateReady = !isNil(viewTemplate);
      if (saveEntityLoading || !isFieldsReady || !isViewTemplateReady) {
        return 'loading';
      } else if (!isEmpty(errors)) {
        return 'error';
      } else if (prevStatus !== 'success') {
        return 'input';
      } else {
        return prevStatus;
      }
    });
  }, [errors, saveEntityLoading, status, fields, viewTemplate]);

  useEffect(() => {
    if (isValid) {
      setSaveAttemptedWithLocalErrors(false);
    }
  }, [isValid]);

  const save = useCallback(async () => {
    if (!isValid) {
      setSaveAttemptedWithLocalErrors(true);
      return;
    }
    setSaveAttemptedWithLocalErrors(false);

    if (shouldBatchCreate) {
      try {
        const result = await batchCreate({
          workflowTemplateId: workflowTemplateId ?? '',
          fields: toFieldJson(fields, workflowTemplate),
        });
        const entities = result?.data?.createRecurringEntities?.workflowEntitiesResult?.entities;
        const entity = entities?.[0];
        if (!entity) {
          throw new Error('No entity returned from batch create');
        }
        setStatus('success');
        setSavedEntity(entity);
      } catch (error) {
        setBatchCreateError(error);
        setStatus('error');
      }
    } else {
      try {
        const result = await saveEntity(fields);
        const entity = result.data?.createWorkflowEntity?.entity;
        if (!entity) {
          throw new Error('No entity returned from saveEntity');
        }
        setStatus('success');
        setSavedEntity(entity);
      } catch (error) {
        setStatus('error');
      }
    }
  }, [
    fields,
    saveEntity,
    isValid,
    shouldBatchCreate,
    batchCreate,
    workflowTemplateId,
    workflowTemplate,
  ]);

  const resetForm = useCallback(() => {
    resetFields();
    setStatus('input');
  }, [resetFields, setStatus]);

  return {
    components,
    fields,
    errors,
    viewTemplateTitle: viewTemplate?.name ?? '',
    viewTemplate,
    updateField,
    onPressSave: save,
    isLoading: status === 'loading',
    status,
    isDirty,
    setStatus,
    resetForm,
    savedEntity,
  };
}
